import { Helmet } from 'react-helmet-async';

interface SEOProps {
    title: string;
    description: string;
    image?: string;
    /** サイトルートからのパス (例: '/privacy-policy')。canonical と og:url に使う */
    path?: string;
    /** 検索結果に出したくないページ(サンクスページ等)で true にする */
    noindex?: boolean;
    /**
     * OGPの種別。ブログ記事は 'article' にする。
     * SNS側が記事として扱い、公開日・更新日を読めるようになる。
     */
    type?: 'website' | 'article';
    /** type='article' のときだけ使う。'YYYY-MM-DD' */
    publishedTime?: string;
    modifiedTime?: string;
    /**
     * ページ固有の構造化データ(JSON-LD)。FAQPage やパンくずなど
     * 「そのページにしか当てはまらないもの」をここから注入する。
     *
     * Organization / WebSite / SoftwareApplication のようなサイト共通のものは
     * public/index.html に静的に置いてある(CSRのためクローラーがJSを実行しなくても
     * 読めるようにする狙い)。ページ固有のものを静的HTMLに置くと全ページに出てしまい
     * 実態と食い違うため、そちらはこの props 経由で当該ページにだけ出力する。
     */
    jsonLd?: object | object[];
}

const SITE_NAME = 'OHANA AI';
export const SITE_ORIGIN = 'https://ohana-ai.jp';
const DEFAULT_OGP_IMAGE = `${SITE_ORIGIN}/ogp.png`;

/**
 * パンくずリストの構造化データを組み立てる。
 * 下層ページで「ホーム > ○○」を検索結果に表示させ、階層を検索エンジンに伝えるために使う。
 *
 * @param trail ホームより下の階層。[{ name: '機能詳細', path: '/features' }] のように渡す
 */
export const buildBreadcrumb = (trail: { name: string; path: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
        {
            '@type': 'ListItem',
            position: 1,
            name: 'ホーム',
            item: `${SITE_ORIGIN}/`
        },
        ...trail.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 2,
            name: item.name,
            item: `${SITE_ORIGIN}${item.path}`
        }))
    ]
});

// 児発・放デイ、個別支援計画書に特化したメタタグ構成
export const SEO = ({ title, description, image, path = '/', noindex = false, type = 'website', publishedTime, modifiedTime, jsonLd }: SEOProps) => {
    // SNSと検索エンジンは相対URLを解決できないため、絶対URLに正規化する
    const canonicalUrl = `${SITE_ORIGIN}${path}`;
    const ogpImage = image ? (image.startsWith('http') ? image : `${SITE_ORIGIN}${image}`) : DEFAULT_OGP_IMAGE;
    const structuredData = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />

            {/* 重複URL(www有無・末尾スラッシュ等)で評価が分散しないよう正規URLを明示 */}
            <link rel="canonical" href={canonicalUrl} />
            {noindex && <meta name="robots" content="noindex, follow" />}
            {/* スニペットと画像プレビューの制限を外し、検索結果での露出を最大化する */}
            {!noindex && <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}

            {/* OGP設定 (SNSシェア用) */}
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:locale" content="ja_JP" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content={type} />
            {/* 記事のときだけ。SNSやクローラーが「いつの情報か」を判断する材料になる */}
            {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {type === 'article' && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
            <meta property="og:image" content={ogpImage} />
            {/* 幅・高さを明示するとSNS側が先に領域を確保でき、初回シェアでも画像が欠けにくい */}
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={title} />
            <meta property="og:url" content={canonicalUrl} />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogpImage} />
            <meta name="twitter:image:alt" content={title} />

            {/* ページ固有の構造化データ */}
            {structuredData.map((data, index) => (
                <script key={index} type="application/ld+json">
                    {JSON.stringify(data)}
                </script>
            ))}
        </Helmet>
    );
};
